/** Typed client for the DealAlert API. Mirrors packages/server/src/http. */

/**
 * One product inside a sale.
 *
 * Everything past the title and price depends on how it was read: a product the
 * retailer itself listed knows its own SKU, its previous price and whether it
 * is in stock, while one recovered from markup knows a name and a number.
 */
export interface SaleListing {
  title: string | null;
  brand: string | null;
  sku: string | null;
  imageUrl: string | null;
  url: string | null;
  price: {
    cents: number | null;
    wasCents: number | null;
    currency: string | null;
    display: string | null;
    wasDisplay: string | null;
    discountPct: number | null;
    /** What one costs inside the offer — $5.33 of a "3 for $16". */
    inOfferCents: number | null;
    inOfferDisplay: string | null;
  };
  /**
   * What one item of this pack costs — the number people compare on, priced at
   * the offer where there is one. `basis` says which.
   */
  unit?: {
    count: number | null;
    perItemCents: number | null;
    /** Bare money — "$0.44". Pair with `count` to render "12 × $0.44". */
    perItemDisplay: string | null;
    basis?: 'offer' | 'shelf';
    /** The same maths on one case at its ordinary price, for the toggle. */
    shelfPerItemCents?: number | null;
    shelfPerItemDisplay?: string | null;
  };
  /** Null means the shop did not say; never render that as "out of stock". */
  inStock: boolean | null;
  source: string | null;
}

/**
 * The conditions attached to an offer.
 *
 * `limitStated` is the load-bearing one: a missing limit and an unread page
 * look identical in the data and mean opposite things to a shopper. Having
 * read the terms and found no cap is worth saying; having never looked is not.
 */
export interface OfferTerms {
  headline: string | null;
  terms: string | null;
  limit: number | null;
  limitStated: boolean;
  channel: 'online' | 'in_store' | 'both' | null;
  requiresClipping: boolean | null;
  requiresMembership: boolean | null;
}

export function offerConditions(offer: OfferTerms): string[] {
  const out: string[] = [];
  if (offer.limit) out.push(`Limit ${offer.limit}`);
  else if (offer.limitStated) out.push('No limit stated');
  if (offer.requiresMembership) out.push('Needs an account');
  if (offer.requiresClipping) out.push('Must clip first');
  if (offer.channel === 'both') out.push('In store & online');
  else if (offer.channel === 'in_store') out.push('In store only');
  else if (offer.channel === 'online') out.push('Online only');
  return out;
}

export interface Deal {
  id: string;
  title: string;
  itemName: string;
  brand: string | null;
  category: string;
  categoryLabel: string;
  categoryEmoji: string;
  subcategory: string | null;
  tags: string[];
  price: {
    cents: number | null;
    /** Top of a variant range — one page can cover $189.99 to $679.99. */
    maxCents: number | null;
    wasCents: number | null;
    currency: string;
    discountPct: number | null;
    display: string | null;
    maxDisplay: string | null;
    /** "$189.99–$679.99", or null when the deal is a single price. */
    rangeDisplay: string | null;
    wasDisplay: string | null;
    note: string | null;
    /**
     * A multi-buy as numbers rather than prose: "3 for $16" is what turns
     * twenty-one $8.39 12-packs into $5.33 each and 44¢ a can.
     */
    offer: OfferTerms & {
      quantity: number;
      totalCents: number;
      totalDisplay: string | null;
      eachCents: number | null;
      eachDisplay: string | null;
    } | null;
    /** Conditions on a deal that is not a multi-buy — same fields, no count. */
    terms?: OfferTerms | null;
  };
  /**
   * What the pack works out to, for things sold in packs and sizes. Absent
   * fields mean "we could not read a pack size", never zero.
   */
  unit?: {
    count: number | null;
    size: number | null;
    label: string | null;
    perItemCents: number | null;
    /** "$0.92 each" */
    perItemDisplay: string | null;
    /** Cents per millilitre / gram / item — the value thresholds compare on. */
    perMeasureCents: number | null;
    measureDim: MeasureDim | null;
    /** "$4.30/L" — scaled to the unit a shelf label uses. */
    perMeasureDisplay: string | null;
  };
  /** How many separate posts we have seen for this deal. */
  mentionCount?: number;
  couponCode: string | null;
  merchant: string | null;
  url: string | null;
  imageUrl: string | null;
  /** Every picture of this deal, hero first. */
  images?: string[];
  /** The individual products behind a sale that covers more than one thing. */
  listings?: SaleListing[];
  /**
   * The sale as a whole: how many products, what they span, and how exactly we
   * know. Null for an ordinary single-product deal.
   */
  sale?: {
    count: number;
    lowCents: number | null;
    highCents: number | null;
    priceRangeDisplay: string | null;
    /**
     * True when the retailer itself listed what the offer covers. A scraped
     * list is however much of the sale a parser could see, and the wording
     * has to say which one this is.
     */
    exact: boolean;
    source: string | null;
  } | null;
  availability: { online: boolean; inStore: boolean; stockStatus: string };
  locations: { id: string; label: string | null; address: string | null; city: string | null; lat: number; lng: number }[];
  distanceM: number | null;
  distanceDisplay: string | null;
  postedAt: string;
  expiresAt: string | null;
  status: string;
  confidence: number;
  votes: { up: number; down: number; score: number; heat: number; mine: number | null };
  /**
   * What a vision model found in the listing's photographs, and whether it has
   * finished looking.
   *
   * `state` is sent explicitly rather than inferred from an empty `items`
   * array, because "still analysing" and "analysed, found nothing" look
   * identical to a client and mean opposite things. See vision/stage.ts.
   */
  vision?: {
    state: 'none' | 'pending' | 'running' | 'done' | 'failed' | 'skipped';
    /** Keep polling and show a working state while this is true. */
    pending: boolean;
    model: string | null;
    error: string | null;
    startedAt: string | null;
    estValue: { cents: number | null; display: string | null };
    items: DealItem[];
  };
  source: string | null;
}

export interface DealItem {
  id: string;
  name: string;
  brand: string | null;
  category: string | null;
  categoryLabel: string | null;
  subcategory: string | null;
  /** Read by OCR, not by the vision model — see vision/ocr.ts for why. */
  modelNumber: string | null;
  serial: string | null;
  condition: string | null;
  photoUrl: string | null;
  confidence: number;
  /** A range over a listing count, or null. Never a single number. */
  resale: {
    lowCents: number;
    highCents: number;
    currency: string;
    display: string;
    count: number | null;
    source: string | null;
  } | null;
}

export type MeasureDim = 'volume' | 'weight' | 'count';

/**
 * A standing alert on a thing: "Red Bull under $1.50 a can", "soda under $4 a
 * litre", "Lay's at 30% off". Called a hunt on the wire for historical reasons
 * — it started life as "tell me when a mini fridge shows up free near me" —
 * and shown as an Alert everywhere a person can read it.
 */
export interface Hunt {
  id: string;
  name: string;
  query: string;
  /** The phrasings actually matched — shown so a surprise alert is explicable. */
  matchTerms: string[];
  excludeTerms: string[];
  categories: string[];
  subcategories: string[];
  /** Matched against the deal's brand field, not its title. */
  brands: string[];

  // Scope: the three ways a deal can be in range, independently switchable.
  radarId: string | null;
  includeNearby: boolean;
  includeOnline: boolean;
  includePinnedStores: boolean;
  storePinIds: string[];
  retailers: string[];

  // Thresholds.
  freeOnly: boolean;
  maxPriceCents: number | null;
  /** Cents per item in the pack — the one that means something for a consumable. */
  maxUnitPriceCents: number | null;
  /** Cents per base unit (ml / g / item). Set via measurePreset, not by hand. */
  maxMeasurePriceCents: number | null;
  measureDim: MeasureDim | null;
  /** Which unit the ceiling was typed in, e.g. 'per_l'. */
  measurePreset: string | null;
  /** The ceiling in that unit — 400 with 'per_l' means "$4.00 a litre". */
  measurePresetCents: number | null;
  minUnitCount: number | null;
  minDiscountPct: number | null;
  minValueCents: number | null;
  minScore: number;
  requireInStock: boolean;

  // Delivery.
  notify: boolean;
  maxAlertsPerDay: number | null;
  provisionWatches: boolean;
  active: boolean;

  matchCount: number;
  lastMatchedAt: string | null;
  createdAt: string;
}

/** A ready-made alert, already configured. */
export interface AlertSuggestion {
  id: string;
  name: string;
  query: string;
  emoji: string;
  detail: string;
  categories?: string[];
  subcategories?: string[];
  brands?: string[];
  excludeTerms?: string[];
  maxPriceCents?: number;
  maxUnitPriceCents?: number;
  maxMeasurePriceCents?: number;
  measureDim?: MeasureDim;
  minDiscountPct?: number;
  minUnitCount?: number;
  maxAlertsPerDay?: number;
}

/** How a per-measure ceiling can be expressed, and the server's conversion. */
export interface MeasurePreset {
  id: string;
  label: string;
  dim: MeasureDim;
  perBase: number;
}

export interface HuntPreview {
  matchesLast7Days: number;
  estimatedPerDay: number;
  /** What you'd actually be sent, once the daily ceiling is applied. */
  cappedPerDay: number;
  matchTerms: string[];
  examples: string[];
}

export interface HuntHit extends Deal {
  /**
   * How it matched: the post's own words, a model finding it in a photo, or a
   * product inside the sale the post points at.
   */
  via: 'text' | 'vision' | 'listings';
  pinned: boolean;
  matchedAt: string;
}

export interface Radar {
  id: string;
  name: string;
  kind: 'home' | 'drop';
  lat: number;
  lng: number;
  radiusM: number;
  radiusDisplay: string;
  includeOnline: boolean;
  categories: string[];
  keywords: string[];
  excludeKeywords: string[];
  maxPriceCents: number | null;
  minDiscountPct: number | null;
  minScore: number;
  notify: boolean;
  liveActivity: boolean;
  active: boolean;
}

export interface Me {
  id: string;
  /** Null for a guest — the web never collects one. */
  email: string | null;
  displayName: string;
  handle: string | null;
  currency: string;
  timezone: string;
  counts: { radars: number; devices: number };
}

export interface Category {
  id: string;
  label: string;
  emoji: string;
  subcategories: { id: string; label: string }[];
}

const TOKEN_KEY = 'dealalert.token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (body as { error?: { code: string; message: string } } | null)?.error;
    // A dead session should log the user out rather than loop on 401s.
    if (res.status === 401) setToken(null);
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `Request failed (${res.status})`);
  }
  return body as T;
}

export interface FeedParams {
  scope?: 'near' | 'all';
  sort?: 'new' | 'hot' | 'discount';
  category?: string;
  q?: string;
  inStore?: boolean;
  minDiscount?: number;
  cursor?: string;
  limit?: number;
}

export const api = {
  /**
   * Provision an account without asking for anything. There is no sign-in on
   * the web — see `ensureSession` for what that costs and why it's the trade.
   */
  guest: () =>
    request<{ token: string }>('/v1/auth/guest', {
      method: 'POST',
      body: JSON.stringify({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
    }),

  me: () => request<Me>('/v1/me'),

  deals: (params: FeedParams = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '' && v !== null) qs.set(k, String(v));
    }
    return request<{ deals: Deal[]; nextCursor: string | null }>(`/v1/deals?${qs}`);
  },

  deal: (id: string) => request<{ deal: Deal }>(`/v1/deals/${id}`),

  vote: (id: string, value: 1 | -1 | 0, reason?: string) =>
    request<{ votes: { up: number; down: number; score: number; mine: number | null }; status: string }>(
      `/v1/deals/${id}/vote`,
      { method: 'POST', body: JSON.stringify({ value, reason }) },
    ),

  hunts: () => request<{ hunts: Hunt[] }>('/v1/hunts'),

  createHunt: (body: { query: string } & Partial<Hunt>) =>
    request<{ hunt: Hunt }>('/v1/hunts', { method: 'POST', body: JSON.stringify(body) }),

  updateHunt: (id: string, body: Partial<Hunt>) =>
    request<{ hunt: Hunt }>(`/v1/hunts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteHunt: (id: string) => request<null>(`/v1/hunts/${id}`, { method: 'DELETE' }),

  /** Ready-made alerts, plus the units a per-measure ceiling can be set in. */
  alertSuggestions: () =>
    request<{ suggestions: AlertSuggestion[]; measures: MeasurePreset[] }>('/v1/hunts/suggestions'),

  /**
   * What this alert would have caught last week. The only way to tell a
   * threshold that is strict from one that is broken before committing to it.
   */
  previewHunt: (body: { query: string } & Partial<Hunt>) =>
    request<HuntPreview>('/v1/hunts/preview', { method: 'POST', body: JSON.stringify(body) }),

  huntHits: (id: string, includeInactive = false) =>
    request<{ hits: HuntHit[] }>(`/v1/hunts/${id}/hits?includeInactive=${includeInactive}`),

  pinHit: (huntId: string, dealId: string, pinned: boolean) =>
    request<{ pinned: boolean }>(`/v1/hunts/${huntId}/hits/${dealId}/pin`, {
      method: 'POST',
      body: JSON.stringify({ pinned }),
    }),

  radars: () => request<{ radars: Radar[] }>('/v1/radars'),

  createRadar: (body: Partial<Radar> & { name: string; lat: number; lng: number }) =>
    request<{ radar: Radar }>('/v1/radars', { method: 'POST', body: JSON.stringify(body) }),

  updateRadar: (id: string, body: Partial<Radar>) =>
    request<{ radar: Radar }>(`/v1/radars/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteRadar: (id: string) => request<{ ok: true }>(`/v1/radars/${id}`, { method: 'DELETE' }),

  previewRadar: (body: { name: string; lat: number; lng: number; radiusM: number; includeOnline: boolean; categories: string[] }) =>
    request<{ matchesLast7Days: number; estimatedPerDay: number }>('/v1/radars/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  taxonomy: () => request<{ categories: Category[] }>('/v1/taxonomy'),

  stats: () =>
    request<{ active_deals: number; deals_24h: number; sources: number; byCategory: { category: string; count: number }[] }>(
      '/v1/stats',
    ),

  registerWebPush: (subscription: PushSubscriptionJSON) =>
    request<{ deviceId: string }>('/v1/devices', {
      method: 'POST',
      body: JSON.stringify({
        platform: 'web',
        webPush: {
          endpoint: subscription.endpoint,
          p256dh: subscription.keys?.p256dh,
          auth: subscription.keys?.auth,
        },
        locale: navigator.language,
      }),
    }),
};

/**
 * Get to a usable session without ever asking the visitor for anything.
 *
 * A stored token is used as-is; if it has expired or been revoked the server
 * answers 401, `request` clears it, and we provision a fresh guest rather than
 * bouncing the person to a sign-in screen they have no credential for. The
 * consequence to be clear-eyed about: the token is the account, so clearing
 * site data starts over with empty radars and alerts.
 */
export async function ensureSession(): Promise<Me> {
  if (getToken()) {
    try {
      return await api.me();
    } catch (err) {
      // Anything other than a dead session is a real failure — a network drop
      // shouldn't silently orphan the radars behind a brand new account.
      if (!(err instanceof ApiError) || err.status !== 401) throw err;
    }
  }
  const { token } = await api.guest();
  setToken(token);
  return api.me();
}
